import Div from "@jumbo/shared/Div";
import { Button, MenuItem, Select, Typography, Autocomplete, TextField, Box } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { getReceivePendingOrderReport } from "app/redux/actions/reportAction";
import { downloadReceivePendingOrderReport } from "app/services/apis/ReportApis/AllReport/receivePending";
import { dateFun } from "app/utils/constants/functions";
import dayjs from "dayjs";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getFactoryNameList } from "app/services/apis/ListApi/factoryNameList";
import { getPortListNoPerm } from "app/services/apis/ListApi/portListNoPerm";
import { getSalesPersonList } from "app/services/apis/salesPersonDropdown";
import Swal from "sweetalert2";

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 15 }, (_, i) => String(currentYear - i));
const MONTHS = [
  { label: "January", value: "1" },
  { label: "February", value: "2" },
  { label: "March", value: "3" },
  { label: "April", value: "4" },
  { label: "May", value: "5" },
  { label: "June", value: "6" },
  { label: "July", value: "7" },
  { label: "August", value: "8" },
  { label: "September", value: "9" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
];

const ReceivePendingOrderReportsFilter = () => {
  const [inputs, setInputs] = useState({});
  const [factoryList, setFactoryList] = useState([]);
  const [portList, setPortList] = useState([]);
  const [salesPersonList, setSalesPersonList] = useState([]);
  const { receivePendingOrder } = useSelector((state) => state.reportReducer);
  const dispatch = useDispatch();

  useEffect(() => {
    const loadData = async () => {
      try {
        const [factories, ports, salesPersons] = await Promise.all([
          getFactoryNameList(),
          getPortListNoPerm(),
          getSalesPersonList(),
        ]);
        setFactoryList(factories || []);
        setPortList(ports || []);
        setSalesPersonList(
          (salesPersons || []).filter((person) =>
            /^\d+$/.test(String(person?.id ?? "").trim())
          )
        );
      } catch (error) {
        console.error(error);
      }
    };
    loadData();
  }, []);

  const hasDateRange = Boolean(inputs.from || inputs.to);
  const hasMonthYear = Boolean(inputs.year || inputs.month);

  const validateFilters = () => {
    if ((inputs.from && !inputs.to) || (!inputs.from && inputs.to)) {
      Swal.fire({ icon: "warning", title: "Select From and To Date" });
      return false;
    }
    if (inputs.month && !inputs.year) {
      Swal.fire({ icon: "warning", title: "Select Year" });
      return false;
    }
    return true;
  };

  const handleFilter = async () => {
    if (!validateFilters()) return;
    const requestParams = {
      ...inputs,
      salesman_name: inputs.sales_person?.full_name || "",
      port_no_and_name: inputs.port_no_and_name || "",
    };
    const ok = await dispatch(getReceivePendingOrderReport("", requestParams));
    if (!ok) {
      Swal.fire({ icon: "error", title: "No Result Found" });
    }
  };

  const handleClear = () => {
    setInputs({});
    dispatch(getReceivePendingOrderReport("", {}));
  };

  return (
    <>
      <Div sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 3 }}>
        <Div sx={{ width: "23%" }}>
          <Typography variant="h5">Job Work Issued Date From</Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              format="DD/MM/YY"
              disabled={hasMonthYear}
              value={inputs?.from ? dayjs(inputs.from) : null}
              onChange={(newDate) =>
                setInputs((values) => ({
                  ...values,
                  from: newDate ? dateFun(newDate) : "",
                  year: "",
                  month: "",
                }))
              }
              sx={{
                width: "100%",
                "& .MuiInputBase-root": {
                  height: "39px",
                  flexDirection: "unset",
                },
              }}
            />
          </LocalizationProvider>
        </Div>
        <Div sx={{ width: "23%" }}>
          <Typography variant="h5">Job Work Issued Date To</Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              format="DD/MM/YY"
              disabled={hasMonthYear}
              value={inputs?.to ? dayjs(inputs.to) : null}
              onChange={(newDate) =>
                setInputs((values) => ({
                  ...values,
                  to: newDate ? dateFun(newDate) : "",
                  year: "",
                  month: "",
                }))
              }
              sx={{
                width: "100%",
                "& .MuiInputBase-root": {
                  height: "39px",
                  flexDirection: "unset",
                },
              }}
            />
          </LocalizationProvider>
        </Div>
        <Div sx={{ width: "23%" }}>
          <Typography variant="h5">Year</Typography>
          <Select
            size="small"
            sx={{ width: "100%" }}
            value={inputs.year || ""}
            name="year"
            disabled={hasDateRange}
            onChange={(e) =>
              setInputs((values) => ({
                ...values,
                year: e.target.value,
                from: "",
                to: "",
              }))
            }
          >
            <MenuItem value="">Select Year</MenuItem>
            {YEARS.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </Div>
        <Div sx={{ width: "23%" }}>
          <Typography variant="h5">Month</Typography>
          <Select
            size="small"
            sx={{ width: "100%" }}
            value={inputs.month || ""}
            name="month"
            disabled={hasDateRange}
            onChange={(e) =>
              setInputs((values) => ({
                ...values,
                month: e.target.value,
                from: "",
                to: "",
              }))
            }
          >
            <MenuItem value="">Select Month</MenuItem>
            {MONTHS.map((month) => (
              <MenuItem key={month.value} value={month.value}>
                {month.label}
              </MenuItem>
            ))}
          </Select>
        </Div>
        <Div sx={{ width: "23%" }}>
          <Typography variant="h5">Factory Name</Typography>
          <Autocomplete
            size="small"
            options={factoryList || []}
            value={inputs.factory || null}
            getOptionLabel={(option) => option?.factory_name || ""}
            isOptionEqualToValue={(option, value) =>
              String(option?.id) === String(value?.id)
            }
            onChange={(e, newValue) =>
              setInputs((prev) => ({
                ...prev,
                factory: newValue || null,
                factory_name: newValue?.factory_name || "",
              }))
            }
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                {option?.factory_name}
              </Box>
            )}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" />
            )}
          />
        </Div>
        <Div sx={{ width: "23%" }}>
          <Typography variant="h5">Port Number & Name</Typography>
          <Autocomplete
            size="small"
            options={portList || []}
            value={inputs.port_no_and_name || null}
            getOptionLabel={(option) => option || ""}
            isOptionEqualToValue={(option, value) => option === value}
            onChange={(e, newValue) =>
              setInputs((prev) => ({
                ...prev,
                port_no_and_name: newValue || null,
              }))
            }
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                {option}
              </Box>
            )}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" />
            )}
          />
        </Div>
        <Div sx={{ width: "23%" }}>
          <Typography variant="h5">Sales Person Name</Typography>
          <Autocomplete
            size="small"
            options={salesPersonList || []}
            value={inputs.sales_person || null}
            getOptionLabel={(option) => {
              if (option instanceof Object && !Array.isArray(option)) {
                return option?.full_name || "";
              }
              return option || "";
            }}
            isOptionEqualToValue={(option, value) =>
              String(option?.id) === String(value?.id)
            }
            onChange={(e, newValue) =>
              setInputs((prev) => ({
                ...prev,
                sales_person: newValue || null,
              }))
            }
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                {option?.full_name}
              </Box>
            )}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" />
            )}
          />
        </Div>
      </Div>

      <Div
        sx={{
          display: "flex",
          alignItems: "center",
          mt: 4,
          justifyContent: "space-between",
        }}
      >
        <Div>
          <Button
            size="small"
            variant="outlined"
            sx={{ p: 1, pl: 4, pr: 4, marginRight: 2 }}
            onClick={handleFilter}
          >
            Apply
          </Button>
          <Button
            size="small"
            variant="outlined"
            sx={{ p: 1, pl: 4, pr: 4 }}
            onClick={handleClear}
          >
            Clear
          </Button>
        </Div>
        <Button
          disabled={!receivePendingOrder || receivePendingOrder.length == 0}
          variant="contained"
          onClick={async () => {
            if (!validateFilters()) return;
            const requestParams = {
              ...inputs,
              salesman_name: inputs.sales_person?.full_name || "",
              port_no_and_name: inputs.port_no_and_name || "",
            };
            const data = await downloadReceivePendingOrderReport(requestParams);
            if (data) {
              window.open(data, "_blank");
            } else {
              Swal.fire({ icon: "error", title: "No Result Found" });
            }
          }}
        >
          Export CSV
        </Button>
      </Div>
    </>
  );
};

export default ReceivePendingOrderReportsFilter;
